import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard,
  Building2,
  Shield,
  Megaphone,
  DollarSign,
  Users,
  Eye,
  Upload,
  Settings,
  ClipboardList,
  LogOut,
  Activity,
  X,
  GitCompare,
  History,
  Bell,
  User,
  Target,
  Compass,
  Mountain,
  TrendingUp,
  FileSpreadsheet
} from 'lucide-react';

const Sidebar = ({ onClose }) => {
  const { user, logout, isAdmin, isBoard } = useAuth();
  const location = useLocation();

  const mainNavItems = [
    { path: '/scorecard', icon: LayoutDashboard, label: 'Executive Scorecard' },
    { path: '/compare', icon: GitCompare, label: 'Period Comparison' },
    { path: '/mga', icon: Shield, label: 'MGA' },
    { path: '/marketing', icon: Megaphone, label: 'Marketing' },
    { path: '/finance', icon: DollarSign, label: 'Finance' },
    { path: '/people', icon: Users, label: 'People' },
  ];

  const brokerageNavItems = [
    { path: '/brokerage', icon: Building2, label: 'Brokerage Dashboard' },
    { path: '/brokerage/pipeline', icon: TrendingUp, label: 'Producer Pipeline' },
    { path: '/brokerage/weekly-upload', icon: Upload, label: 'Weekly Upload' },
  ];

  const strategyNavItems = [
    { path: '/strategy/svep', icon: Target, label: 'SVEP' },
    { path: '/strategy/rocks', icon: Mountain, label: 'Quarterly Rocks' },
    { path: '/strategy', icon: Compass, label: 'Strategy Dashboard' },
  ];

  const secondaryNavItems = [
    { path: '/board-view', icon: Eye, label: 'Board View' },
  ];

  const adminNavItems = [
    { path: '/upload', icon: Upload, label: 'Upload Center' },
    { path: '/plan-upload', icon: FileSpreadsheet, label: 'Annual Plan Upload' },
    { path: '/kpi-definitions', icon: Settings, label: 'KPI Definitions' },
    { path: '/action-log', icon: ClipboardList, label: 'Action Log' },
    { path: '/timeline', icon: History, label: 'Activity Timeline' },
    { path: '/users', icon: Users, label: 'User Management', adminOnly: true },
    { path: '/settings/notifications', icon: Bell, label: 'Notification Settings', adminOnly: true },
  ];

  const NavItem = ({ item }) => {
    const isActive = location.pathname === item.path;
    return (
      <NavLink
        to={item.path}
        onClick={onClose}
        data-testid={`nav-${item.path.replace('/', '')}`}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-150 ${
          isActive
            ? 'bg-[#EFF6FF] text-[#2563EB]'
            : 'text-[#111827] hover:bg-[#F3F4F6]'
        }`}
      >
        <item.icon 
          className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-[#2563EB]' : 'text-[#6B7280]'}`} 
          strokeWidth={1.5} 
        />
        <span className="flex-1 truncate">{item.label}</span>
      </NavLink>
    );
  };

  return (
    <aside className="h-screen w-64 bg-white border-r border-[#E5E7EB] flex flex-col">
      {/* Logo */}
      <div className="h-16 flex items-center px-4 border-b border-[#E5E7EB]">
        <div className="flex items-center gap-2 flex-1">
          <img 
            src="https://customer-assets.emergentagent.com/job_metrics-hub-117/artifacts/yhkjnbgq_63ed5a01637c0a0b27bc74fc_Players-Health_logo-2.svg" 
            alt="Players Health" 
            className="h-8 w-auto"
          />
          <div className="flex items-center gap-1.5">
            <div className="w-px h-6 bg-[#E5E7EB]"></div>
            <div className="flex items-center gap-1">
              <Activity className="w-4 h-4 text-[#2563EB]" strokeWidth={2} />
              <span className="text-[#111827] font-semibold text-sm tracking-tight">Pulse</span>
            </div>
          </div>
        </div>
        {/* Close button for mobile */}
        <button 
          onClick={onClose}
          className="lg:hidden p-1 text-[#6B7280] hover:text-[#111827]"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        {/* Strategy Section - Admin/ELT only */}
        {!isBoard() && (
          <div className="mb-6">
            <p className="px-3 mb-2 text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider">
              Strategy & Execution
            </p>
            <div className="space-y-1">
              {strategyNavItems.map((item) => (
                <NavItem key={item.path} item={item} />
              ))}
            </div>
          </div>
        )}

        {/* Main Dashboards */}
        <div className="mb-6">
          <p className="px-3 mb-2 text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider">
            Dashboards
          </p>
          <div className="space-y-1">
            {mainNavItems.map((item) => (
              <NavItem key={item.path} item={item} />
            ))}
          </div>
        </div>

        {/* Brokerage Section - Admin/ELT only */}
        {!isBoard() && (
          <div className="mb-6">
            <p className="px-3 mb-2 text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider">
              Brokerage
            </p>
            <div className="space-y-1">
              {brokerageNavItems.map((item) => (
                <NavItem key={item.path} item={item} />
              ))}
            </div>
          </div>
        )}

        {/* Board View */}
        <div className="mb-6">
          <p className="px-3 mb-2 text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider">
            Reports
          </p>
          <div className="space-y-1">
            {secondaryNavItems.map((item) => (
              <NavItem key={item.path} item={item} />
            ))}
          </div>
        </div>

        {/* Admin Section - Only for Admin/ELT */}
        {!isBoard() && (
          <div className="mb-6">
            <p className="px-3 mb-2 text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider">
              Management
            </p>
            <div className="space-y-1">
              {adminNavItems
                .filter(item => !item.adminOnly || isAdmin())
                .map((item) => (
                  <NavItem key={item.path} item={item} />
                ))}
            </div>
          </div>
        )}
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-[#E5E7EB]">
        <NavLink
          to="/profile"
          onClick={onClose}
          className={`flex items-center gap-3 mb-3 p-2 -mx-2 rounded-lg hover:bg-[#F3F4F6] transition-colors ${
            location.pathname === '/profile' ? 'bg-[#EFF6FF]' : ''
          }`}
        >
          <div className="w-9 h-9 rounded-full bg-[#F3F4F6] flex items-center justify-center flex-shrink-0">
            <span className="text-[#6B7280] text-sm font-medium">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#111827] truncate">{user?.name}</p>
            <p className="text-xs text-[#6B7280] capitalize">{user?.role}</p>
          </div>
          <User className="w-4 h-4 text-[#9CA3AF]" />
        </NavLink>
        <button
          onClick={() => {
            logout();
            onClose?.();
          }}
          data-testid="logout-btn"
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-[#6B7280] hover:text-[#111827] hover:bg-[#F3F4F6] rounded-md transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Sign out
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
